import { Component, Input, OnInit } from '@angular/core';
import Swal from 'sweetalert2';
import { LotteryService, LotteryDetails, LotteryRerunResultRow, LotteryRunSummary, LotteryDetailsParticipant } from 'src/app/services/lottery.service';
import { LotteryDrillDownContext } from '../lottery-dashboard/lottery-dashboard.component';

@Component({
  selector: 'app-lottery-details',
  templateUrl: './lottery-details.component.html',
  styleUrls: ['./lottery-details.component.scss']
})
export class LotteryDetailsComponent implements OnInit {

  @Input() drillData: LotteryDrillDownContext;
  @Input() divisionId: number;
  @Input() subDivisionId: number;
  @Input() villageId: number;

  loading = true;
  details: LotteryDetails;

  participantColumns: string[] = ['rank', 'applicationId', 'farmerName', 'community', 'status'];
  private participantSearch: { [refNo: string]: string } = {};

  // Ref no of whichever run's Rerun button is currently in flight - only that run's button/panel shows it.
  reruningRefNo: string = null;

  // Rerun re-check result, scoped to exactly one run at a time (the last one re-run) - rendered inside
  // that run's own expansion panel, not a separate section.
  rerunResultRunRefNo: string = null;
  rerunResultColumns: string[] = ['rank', 'applicationId', 'community', 'lotteryScore', 'status'];
  rerunSelectedParticipants: LotteryRerunResultRow[] = [];
  rerunNotSelectedParticipants: LotteryRerunResultRow[] = [];
  // Whether the last rerun's recorded ranks still matched on re-check - drives the banner
  // shown at the top of the rerun-result block (see executeRerun below).
  rerunAllMatched: boolean = null;

  constructor(private lotteryService: LotteryService) { }

  allParticipants(run: LotteryRunSummary): LotteryDetailsParticipant[] {
    return [...(run.selectedParticipants || []), ...(run.notSelectedParticipants || [])]
      .sort((a, b) => a.rank - b.rank);
  }

  // --- Per-run helpers ---

  // Community-wise applied/selected/waiting split for this run, derived purely from the
  // participants already returned for it - no extra API call, no assumption about which
  // community values the backend sends.
  communityBreakdown(run: LotteryRunSummary): Array<{ community: string; applied: number; selected: number; waiting: number }> {
    const byCommunity = new Map<string, { community: string; applied: number; selected: number; waiting: number }>();
    this.allParticipants(run).forEach(p => {
      const key = p.community || 'Unspecified';
      if (!byCommunity.has(key)) {
        byCommunity.set(key, { community: key, applied: 0, selected: 0, waiting: 0 });
      }
      const row = byCommunity.get(key);
      row.applied++;
      if (p.selected) {
        row.selected++;
      } else {
        row.waiting++;
      }
    });
    return Array.from(byCommunity.values()).sort((a, b) => b.applied - a.applied);
  }

  getSearchTerm(run: LotteryRunSummary): string {
    return this.participantSearch[run.lotteryRunRefNo] || '';
  }

  onSearchChange(run: LotteryRunSummary, value: string): void {
    this.participantSearch[run.lotteryRunRefNo] = value;
  }

  
  private matchesTerm(p: LotteryDetailsParticipant, term: string): boolean {
    if (!term) {
      return true;
    }
    return (p.farmerName || '').toLowerCase().includes(term) ||
      (p.applicationId || '').toLowerCase().includes(term) ||
      (p.community || '').toLowerCase().includes(term);
  }

  private filterParticipants(run: LotteryRunSummary, list: LotteryDetailsParticipant[]): LotteryDetailsParticipant[] {
    const term = this.getSearchTerm(run).trim().toLowerCase();
    if (!term) {
      return list;
    }
    return list.filter(p => this.matchesTerm(p, term));
  }

  // Search-filtered variants behind the All / Selected / Waiting tabs.
  allFiltered(run: LotteryRunSummary): LotteryDetailsParticipant[] {
    return this.filterParticipants(run, this.allParticipants(run));
  }

  selectedFiltered(run: LotteryRunSummary): LotteryDetailsParticipant[] {
    return this.filterParticipants(run, [...(run.selectedParticipants || [])].sort((a, b) => a.rank - b.rank));
  }

  waitingFiltered(run: LotteryRunSummary): LotteryDetailsParticipant[] {
    return this.filterParticipants(run, [...(run.notSelectedParticipants || [])].sort((a, b) => a.rank - b.rank));
  }

  // Purely cosmetic keyword mapping for status pills - not a source of truth for status
  // semantics, just colors whatever string the backend already sends.
  statusBadgeClass(status: string): string {
    const value = (status || '').toLowerCase();
    if (value.includes('select') || value.includes('book') || value.includes('complet')) {
      return 'status-badge status-badge-success';
    }
    if (value.includes('wait') || value.includes('pending') || value.includes('next')) {
      return 'status-badge status-badge-warning';
    }
    if (value.includes('cancel') || value.includes('reject') || value.includes('fail')) {
      return 'status-badge status-badge-danger';
    }
    return 'status-badge status-badge-neutral';
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.lotteryService
      .getLotteryDetails(
        this.drillData?.financialYearId, this.drillData?.departmentId, this.drillData?.dbtGoId,
        this.drillData?.dbtSchemeId, this.drillData?.dbtComponentId, this.drillData?.dbtProductId,
        this.drillData?.lotteryRunId, this.divisionId, this.subDivisionId, this.villageId,
        this.drillData?.lotteryDate, true)
      .subscribe((result: LotteryDetails) => {
        this.details = result;
        this.loading = false;
      }, () => {
        this.details = null;
        this.loading = false;
      });
  }

  // One Rerun action per run panel - each call strictly re-checks that one run's own results
  // (see backend RerunLottery), never other runs in the same scope.
  rerunLottery(run: LotteryRunSummary): void {
    if (!run?.lotteryRunRefNo) {
      Swal.fire({ title: 'No Lottery Run', text: 'Load a lottery run before re-running.', icon: 'warning' });
      return;
    }

    Swal.fire({
      title: 'Re-run Lottery?',
      text: `This will re-check lottery run ${run.lotteryRunRefNo} (${run.lotteryRunTs}) against all `
        + `${run.totalApplication} application(s) that took part in it, and show whether the recorded outcome still matches.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, re-run it',
      cancelButtonText: 'Cancel'
    }).then(res => {
      if (res.value) {
        this.executeRerun(run);
      }
    });
  }

  private executeRerun(run: LotteryRunSummary): void {
    this.reruningRefNo = run.lotteryRunRefNo;
    this.lotteryService.rerunLottery(run.lotteryRunRefNo).subscribe({
      next: (response: { isSuccess: boolean; failures?: string[]; value?: { results: LotteryRerunResultRow[]; allMatched: boolean } }) => {
        this.reruningRefNo = null;
        if (!response || !response.isSuccess) {
          const message = response?.failures?.length ? response.failures.join(', ') : 'Failed to re-check the lottery run.';
          Swal.fire({ title: 'Re-check Failed', text: message, icon: 'error' });
          return;
        }

        const results = response.value?.results || [];
        this.rerunResultRunRefNo = run.lotteryRunRefNo;
        this.rerunSelectedParticipants = results.filter(r => r.selected).sort((a, b) => a.rank - b.rank);
        this.rerunNotSelectedParticipants = results.filter(r => !r.selected).sort((a, b) => a.rank - b.rank);

        const allMatched = !!response.value?.allMatched;
        this.rerunAllMatched = allMatched;
        Swal.fire({
          title: allMatched ? 'Re-check Passed' : 'Re-check Found Mismatches',
          text: allMatched
            ? `Every recorded rank for run ${run.lotteryRunRefNo} still matches on re-check.`
            : `Some ranks for run ${run.lotteryRunRefNo} no longer match on re-check - see below.`,
          icon: allMatched ? 'success' : 'warning'
        });
        // Nothing was persisted (see backend RerunLottery), so the header stats and run list
        // above are intentionally left untouched/not reloaded.
      },
      error: () => {
        this.reruningRefNo = null;
        Swal.fire({ title: 'Re-check Failed', text: 'Something went wrong while re-checking the lottery run.', icon: 'error' });
      }
    });
  }
}
